import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { FormatPicker, SelectionClockCard, SelectionFlow, SelectionStepIntro } from "@/shared/components";
import type {
  PerformanceFormatSelection,
  PerformanceOption,
  PerformanceSelectionState,
  PerformanceSelectionStep,
} from "./performance-selection";
import { ALL_CLOCKS, ALL_FORMATS, UNKNOWN_CLOCK } from "./performance-selection";

interface PerformanceSelectionProps {
  selection: PerformanceSelectionState;
  formats: readonly PerformanceOption<PerformanceFormatSelection>[];
  clocks: readonly PerformanceOption<string>[];
  tourPage?: "patterns" | "trends";
  resultsLabel: string;
  onEdit: (step: PerformanceSelectionStep) => void;
  onFormat: (format: PerformanceFormatSelection) => void;
  onClock: (clock: string) => void;
  children?: ReactNode;
}

const parsedClock = (signature: string) => {
  if (signature === UNKNOWN_CLOCK) return null;
  // eslint-disable-next-line security/detect-unsafe-regex -- Numeric fields are bounded and separated by a literal plus.
  const live = signature.match(/^(\d{1,10})(?:\+(\d{1,10}))?$/);
  if (live) return { base: Number(live[1]), increment: Number(live[2] ?? 0) };
  const daily = signature.match(/^(\d+)\/(\d+)$/);
  return daily ? { base: Number(daily[1]) * Number(daily[2]), increment: 0 } : null;
};

const duration = (seconds: number) => {
  if (seconds >= 86_400 && seconds % 86_400 === 0) return `${seconds / 86_400} ${seconds === 86_400 ? "day" : "days"}`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} ${seconds === 60 ? "minute" : "minutes"}`;
  return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
};

const clockPresentation = (signature: string) => {
  if (signature === ALL_CLOCKS) return { glyph: "∞", label: "All clocks" };
  const clock = parsedClock(signature);
  if (!clock) return { glyph: "?", label: "Unknown clock" };
  const base =
    clock.base >= 86_400 && clock.base % 86_400 === 0
      ? clock.base / 86_400
      : clock.base >= 60 && clock.base % 60 === 0
        ? clock.base / 60
        : clock.base;
  return {
    glyph: clock.increment ? `${base} | ${clock.increment}` : String(base),
    label: clock.increment ? `${duration(clock.base)} + ${clock.increment} sec` : duration(clock.base),
  };
};

export const PerformanceSelection = ({
  selection,
  formats,
  clocks,
  tourPage,
  resultsLabel,
  onEdit,
  onFormat,
  onClock,
  children,
}: PerformanceSelectionProps) => {
  const hideSelectionSteps = useUserSettingsStore((state) => state.settings.performanceScopeCollapsed);
  const activeStep = selection.step === "format" ? 1 : selection.step === "clock" ? 2 : 3;
  const formatLabel = selection.format
    ? (formats.find(({ value }) => value === selection.format)?.label ?? selection.format)
    : null;
  const clockSkipped = selection.format === ALL_FORMATS;
  const steps = [
    { key: "format", label: "Format", receipt: formatLabel },
    {
      key: "clock",
      label: "Clock",
      receipt: clockSkipped || !selection.clock ? null : clockPresentation(selection.clock).label,
      skipped: clockSkipped,
    },
    { key: "results", label: resultsLabel, receipt: activeStep === 3 ? "Ready" : null },
  ] as const;

  return (
    <Box
      component="section"
      aria-label="Performance selection"
      data-tour-layer={tourPage ? `${tourPage}:${selection.step}` : undefined}
      data-tour={tourPage ? `${tourPage}-${selection.step}` : undefined}
    >
      <SelectionFlow
        activeStep={activeStep}
        hideSelectionSteps={hideSelectionSteps}
        ariaLabel={`${resultsLabel} selection steps`}
        steps={steps}
        onStep={onEdit}
      >
        {activeStep === 1 && (
          <Box>
            <SelectionStepIntro
              eyebrow="STEP 01 / FORMAT"
              title={
                <>
                  What kind of game
                  <br />
                  do you want to study?
                </>
              }
              description="Choose one format. Your library keeps every date and separates each clock precisely."
              descriptionMaxWidth={520}
            />
            <Box
              component="fieldset"
              data-tour={tourPage ? `${tourPage}-format-picker` : undefined}
              sx={{ border: 0, m: 0, p: 0 }}
            >
              <FormatPicker
                ariaLabel="Game format"
                value={selection.format}
                onChange={onFormat}
                items={formats.map(({ value, label, count }) => ({
                  id: value,
                  label,
                  count,
                }))}
              />
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <SelectionStepIntro
              eyebrow="STEP 02 / CLOCK"
              title={
                <>
                  {formatLabel ?? "Selected"} games,
                  <br />
                  at what pace?
                </>
              }
              description="Choose an exact clock to focus your review."
            />
            <Box
              data-tour={tourPage ? `${tourPage}-clock-picker` : undefined}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(auto-fit,minmax(180px,1fr))" },
                gap: 1.5,
              }}
            >
              {clocks.map((option) => {
                const presentation = clockPresentation(option.value);
                return (
                  <SelectionClockCard
                    key={option.value}
                    active={selection.clock === option.value}
                    count={option.count}
                    glyph={presentation.glyph}
                    label={presentation.label}
                    onClick={() => onClock(option.value)}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {activeStep === 3 && children}
      </SelectionFlow>
    </Box>
  );
};
